// ===========================================
// Canonical shift-time handling (timezone-coherent)
// ===========================================
// A shift's start/end time is stored as the UTC clock-face of the restaurant's
// wall-clock time: 9:00 AM is stored as 09:00:00Z. It is ALWAYS read back in
// UTC, so every viewer (any browser timezone, any server timezone) sees the
// exact same time. This avoids the absolute-instant vs wall-clock mismatch that
// makes a 9 AM shift render as 2 AM for an out-of-zone manager.
//
// Rules:
//  - WRITE: build the ISO to send with wallClockToISO(date, "HH:mm").
//  - DISPLAY: render stored timestamps with formatShiftTime / formatShiftTimeRange.
//  - Never use `new Date(`${date}T${time}`)` (browser-local) for shift times,
//    and never format shift times without `timeZone: 'UTC'`.

/** Build the ISO string to SEND when creating/updating a shift. */
export function wallClockToISO(date: string, time: string): string {
  const [h = '0', m = '0'] = time.split(':');
  return `${date}T${h.padStart(2, '0')}:${m.padStart(2, '0')}:00.000Z`;
}

/** Format a stored shift timestamp for display (reads the UTC clock-face). */
export function formatShiftTime(iso: string | Date): string {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  });
}

/** Format a start–end range, e.g. "9:00 AM - 5:00 PM". */
export function formatShiftTimeRange(start: string | Date, end: string | Date): string {
  return `${formatShiftTime(start)} - ${formatShiftTime(end)}`;
}

/** Extract the wall-clock "HH:mm" from a stored shift timestamp (for editing). */
export function shiftTimeToHHmm(iso: string | Date): string {
  return new Date(iso).toISOString().slice(11, 16);
}

/**
 * Build start/end ISO strings for a shift from a date + wall-clock times,
 * rolling the end to the next day for overnight shifts (end <= start).
 * Returns ISO strings ready to send to the API.
 */
export function wallClockRangeToISO(
  date: string,
  startHHmm: string,
  endHHmm: string
): { startTime: string; endTime: string } {
  const startTime = wallClockToISO(date, startHHmm);
  let endTime = wallClockToISO(date, endHHmm);
  if (new Date(endTime) <= new Date(startTime)) {
    const next = new Date(`${date}T00:00:00.000Z`);
    next.setUTCDate(next.getUTCDate() + 1);
    endTime = wallClockToISO(next.toISOString().slice(0, 10), endHHmm);
  }
  return { startTime, endTime };
}
